import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, of, Subject, throwError } from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  finalize,
  catchError,
  withLatestFrom,
  takeUntil,
} from 'rxjs/operators';
import * as FieldsSelectors from 'src/app/fields/store/selectors';
import * as ManureSpaceActions from 'src/app/manure-space/store/manure-space.actions';

import * as ManureSpaceSelectors from 'src/app/manure-space/store/manure-space.selectors';
import { SiteState } from 'src/app/shared/models';

import * as LegalStandardActions from './fertilization-legal-standard.actions';
import { Employee } from '../fertilization-legal-standards.models';
import { FertilizationLegalStandardsService } from '../services/fertilization-legal-standards.service';

@Injectable()
export class FertilizationLegalStandardEffects implements OnDestroy {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly api = inject(FertilizationLegalStandardsService);

  private readonly destroyed$ = new Subject<boolean>();

  getFertilizationLegalStandards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegalStandardActions.getFertilizationLegalStandards),
      withLatestFrom(
        this.store.select(ManureSpaceSelectors.selectManureSpaceDetails),
        this.store.select(FieldsSelectors.getCropFieldSettings),
      ),
      switchMap(([_, manureSpace, fertilizationSettings]) => {
        if (
          !manureSpace ||
          manureSpace?.cropFieldIds.length !== fertilizationSettings.length ||
          !manureSpace?.cropFieldIds?.length
        ) {
          return of(
            LegalStandardActions.setFertilizationLegalStandards({
              fertilizationLegalStandards: null,
            }),
          );
        }
        return this.api
          .getFertilizationLegalStandards({
            manureSpace: manureSpace,
            fertilizationSettings,
          })
          .pipe(
            catchError((error: HttpErrorResponse) =>
              error.status === 404 ? EMPTY : throwError(error),
            ),
            map((fertilizationLegalStandards) =>
              LegalStandardActions.setFertilizationLegalStandards({
                fertilizationLegalStandards,
              }),
            ),
          );
      }),
    ),
  );

  getLoggedInEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegalStandardActions.getLoggedInEmployee),
      switchMap((_) =>
        this.api.getLoggedInEmployee().pipe(
          map((adviser: Employee) =>
            LegalStandardActions.setLoggedInEmployee({ employee: adviser }),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  public readonly manureSpaceResource$ = this.store.pipe(
    select(ManureSpaceSelectors.selectManureSpaceDetailsResource),
  );
  constructor() {
    this.manureSpaceResource$.pipe(takeUntil(this.destroyed$)).subscribe((resource) => {
      if (resource?.state !== 'loaded' && resource?.state !== 'loading') {
        this.store.dispatch(ManureSpaceActions.getManureSpace());
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
